import React from 'react';
import { useStore } from '../../context/StoreContext';
import { Helmet } from 'react-helmet';

/* The body face is the same in all three storefront styles: the Style tab's font
   only ever changes HEADINGS, and those name it inline where they are built. The
   portal's canvas does the same (`BODY_FONT` in GrapesEditor/index.js). */
const BODY_FONT = "'General Sans', Inter, system-ui, -apple-system, sans-serif";

/* The Style tab stores a bare family name. Two of the three are Google Fonts;
   General Sans is Fontshare, so a css2 URL built from the name returns 400 and
   the face is never fetched. */
const FONT_SOURCES = {
  'general sans': 'https://api.fontshare.com/v2/css?f[]=general-sans@400,500,600,700&display=swap',
  fraunces: 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400..700&display=swap',
  poppins: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
};

const BODY_FONT_URL = FONT_SOURCES['general sans'];

// Anything that is not a plain family name (a `[Seed placeholder]`, a stray
// quote, a whole CSS stack) would make a broken URL, so it loads nothing.
const PLAIN_FAMILY = /^[A-Za-z][A-Za-z0-9 ]{0,48}$/;

const fontStylesheetUrl = (family) => {
  const name = String(family || '').trim();
  if (!name) return '';
  const known = FONT_SOURCES[name.toLowerCase()];
  if (known) return known;
  if (!PLAIN_FAMILY.test(name)) return '';
  return `https://fonts.googleapis.com/css2?family=${name.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
};

/* On `body`, not on this wrapper: the mobile drawer, the toasts and anything else
   mounted outside the React tree sit on body too. No `!important` — inline
   heading fonts and a published `html_content`'s CSS have to keep winning. */
const BODY_FONT_STYLES = `
  body { font-family: ${BODY_FONT}; }
  button, input, optgroup, select, textarea { font-family: inherit; }
`;

export const FontWrapper = ({ children }) => {
  const { pageDetails } = useStore();
  const headingFontUrl = fontStylesheetUrl(pageDetails?.page_data?.colorAndApperances?.font);
  const stylesheets = headingFontUrl && headingFontUrl !== BODY_FONT_URL
    ? [BODY_FONT_URL, headingFontUrl]
    : [BODY_FONT_URL];

  return (
    <div id="fonttype">
      <Helmet>
        {stylesheets.map((href) => <link key={href} rel="stylesheet" href={href} />)}
        <style>{BODY_FONT_STYLES}</style>
      </Helmet>
      {children}
    </div>
  );
};
